import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useArtPrompts } from '@/hooks/useArtPrompts';
import { useKeydates } from '@/hooks/useKeydates';
import { useAICreativeModels } from '@/hooks/useAICreativeModels';
import { useVisualGeneration } from '@/hooks/useVisualGeneration';
import { useSizes, type SizeSpec } from '@/hooks/useSizes';
import { toast } from 'sonner';
import { 
  GENERATION_FUNCTIONS, 
  createGenerationResult, 
  updateGenerationStatus, 
  handleGenerationSuccess, 
  handleGenerationError,
  type GenerationResult,
  type GenerationOptions
} from '@/utils/visualGeneration';
import { supabase } from '@/integrations/supabase/client';

// Helper function to transform database models to the expected format
const transformDatabaseModels = (models: any[]) => {
  const grouped: Record<string, any[]> = {};
  
  models.forEach(model => {
    const groupKey = model.group || model.provider;
    
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    
    grouped[groupKey].push({
      id: model.model_id,
      name: model.model_name,
      description: model.description || '',
      enabled: model.enabled,
      supports: model.supported_types || [],
      provider: model.provider
    });
  });
  
  return grouped;
};

// Helper function to filter models by generation type
const getModelsForTab = (tabName: string, aiModels: Record<string, any[]>, selectedGenerationType?: string) => {
  const filtered: Record<string, any[]> = {};
  const filterType = selectedGenerationType === 'all' ? tabName : (selectedGenerationType || tabName);
  
  Object.entries(aiModels).forEach(([platform, models]) => {
    const enabledModels = models.filter(model => 
      model.supports.includes(filterType) && model.enabled
    );
    
    if (enabledModels.length > 0) {
      filtered[platform] = enabledModels;
    }
  });
  
  return filtered;
};

// Helper function to sort platforms with "Other" last
export const sortPlatforms = (platforms: [string, any[]][]) => {
  return platforms.sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
};

interface VisualsCreationEditorContextType {
  // Open/close state
  isOpen: boolean;
  openEditor: () => void;
  closeEditor: () => void;
  
  // Prompt state
  selectedPromptId: string | null;
  selectedPromptText: string;
  customPrompt: string;
  promptSearchQuery: string;
  setSelectedPromptId: (id: string | null) => void;
  setSelectedPromptText: (text: string) => void;
  setCustomPrompt: (text: string) => void;
  setPromptSearchQuery: (query: string) => void;
  
  // Image state
  selectedFiles: File[];
  firstFrameFiles: File[];
  lastFrameFiles: File[];
  uploadingFrame: 'first' | 'last' | null;
  setSelectedFiles: (files: File[]) => void;
  setFirstFrameFiles: (files: File[]) => void;
  setLastFrameFiles: (files: File[]) => void;
  setUploadingFrame: (frame: 'first' | 'last' | null) => void;
  
  // Modal state
  isImageModalOpen: boolean;
  activeCarouselTab: string;
  isPreviewModalOpen: boolean;
  previewImageUrl: string | null;
  isPreviewVideo: boolean;
  setIsImageModalOpen: (open: boolean) => void;
  setActiveCarouselTab: (tab: string) => void;
  setIsPreviewModalOpen: (open: boolean) => void;
  setPreviewImageUrl: (url: string | null) => void;
  setIsPreviewVideo: (isVideo: boolean) => void;
  
  // Size/dimension state
  selectedSize: 'square' | 'wide' | 'tall';
  selectedVisualSize: SizeSpec | null;
  setSelectedSize: (size: 'square' | 'wide' | 'tall') => void;
  setSelectedVisualSize: (size: SizeSpec | null) => void;
  
  // Model state
  selectedModels: Array<{ platform: string; model: string }>;
  selectedGenerationType: string;
  setSelectedModels: (models: Array<{ platform: string; model: string }>) => void;
  setSelectedGenerationType: (type: string) => void;
  
  // Generation state
  generationMode: 'single' | 'batch';
  maxImages: number;
  isGenerating: boolean;
  progress: number;
  results: GenerationResult[];
  showShortcodePopup: boolean;
  setGenerationMode: (mode: 'single' | 'batch') => void;
  setMaxImages: (max: number) => void;
  setIsGenerating: (generating: boolean) => void;
  setProgress: (progress: number) => void;
  setResults: React.Dispatch<React.SetStateAction<GenerationResult[]>>;
  setShowShortcodePopup: (show: boolean) => void;
  
  // Handlers
  handlePromptSelect: (promptId: string, promptText: string, promptName: string) => void;
  handleCustomPromptChange: (text: string) => void;
  handleModelSelect: (groupKey: string, modelId: string) => void;
  handleGenerate: () => Promise<void>;
  handleSetAsPreview: (result: GenerationResult) => Promise<void>;
  clearSelection: () => void;
  
  // Computed data
  libraryPrompts: any[];
  aiModels: Record<string, any[]>;
  validModelsForTab: Record<string, any[]>;
  sizes: SizeSpec[];
  sizesLoading: boolean;
  promptsLoading: boolean;
  keydateItems: any[];
  keydatesLoading: boolean;
}

const VisualsCreationEditorContext = createContext<VisualsCreationEditorContextType | undefined>(undefined);

export const useVisualsCreationEditor = () => {
  const context = useContext(VisualsCreationEditorContext);
  if (!context) {
    throw new Error('useVisualsCreationEditor must be used within a VisualsCreationEditorProvider');
  }
  return context;
};

export const VisualsCreationEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentBrandId } = useBrandManager();
  const { prompts: allPrompts, isLoading: promptsLoading } = useArtPrompts();
  const { items: keydateItems, isLoading: keydatesLoading } = useKeydates(currentBrandId);
  const { models: dbModels, isLoading: modelsLoading } = useAICreativeModels(currentBrandId || '');
  const { generateVisual, isGenerating: isVisualGenerating, progress: visualProgress } = useVisualGeneration();
  const { sizes, loading: sizesLoading } = useSizes();
  
  // Open/close state
  const [isOpen, setIsOpen] = useState(false);
  
  // Prompt state
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [selectedPromptText, setSelectedPromptText] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [promptSearchQuery, setPromptSearchQuery] = useState('');
  
  // Image state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [firstFrameFiles, setFirstFrameFiles] = useState<File[]>([]);
  const [lastFrameFiles, setLastFrameFiles] = useState<File[]>([]);
  const [uploadingFrame, setUploadingFrame] = useState<'first' | 'last' | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeCarouselTab, setActiveCarouselTab] = useState('search');
  
  // Preview state
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPreviewVideo, setIsPreviewVideo] = useState(false);
  
  // Dimension state
  const [selectedSize, setSelectedSize] = useState<'square' | 'wide' | 'tall'>('square');
  const [selectedVisualSize, setSelectedVisualSize] = useState<SizeSpec | null>(null);
  
  // Model state
  const [selectedModels, setSelectedModels] = useState<Array<{ platform: string; model: string }>>([]);
  const [selectedGenerationType, setSelectedGenerationType] = useState<string>('image2image');
  
  // Generation state
  const [generationMode, setGenerationMode] = useState<'single' | 'batch'>('single');
  const [maxImages, setMaxImages] = useState<number>(4);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [showShortcodePopup, setShowShortcodePopup] = useState(false);
  
  // Transform database models
  const aiModels = useMemo(() => transformDatabaseModels(dbModels), [dbModels]);
  
  // Get models for current tab
  const validModelsForTab = useMemo(() => 
    getModelsForTab('image2image', aiModels, selectedGenerationType), 
    [aiModels, selectedGenerationType]
  );
  
  // Filter prompts
  const libraryPrompts = useMemo(() => {
    return allPrompts?.filter(prompt => {
      if (selectedGenerationType === 'all') return true;
      return prompt.type?.toLowerCase() === selectedGenerationType.toLowerCase();
    }) || [];
  }, [allPrompts, selectedGenerationType]);
  
  // Clear frame files when switching away from morphing
  useEffect(() => {
    if (selectedGenerationType !== 'morphing') {
      setFirstFrameFiles([]);
      setLastFrameFiles([]);
      setUploadingFrame(null);
    }
  }, [selectedGenerationType]);
  
  // Auto-close when navigating away from /visuals-creation
  useEffect(() => {
    if (!location.pathname.startsWith('/visuals-creation') && isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname, isOpen]);
  
  const openEditor = useCallback(() => setIsOpen(true), []);
  const closeEditor = useCallback(() => setIsOpen(false), []);
  
  const handleModelSelect = useCallback((groupKey: string, modelId: string) => {
    const model = aiModels[groupKey]?.find(m => m.id === modelId);
    if (!model) return;
    
    const platform = model.provider || groupKey;
    const existing = selectedModels.find(m => m.platform === platform && m.model === modelId);
    
    if (existing) {
      setSelectedModels(selectedModels.filter(m => !(m.platform === platform && m.model === modelId)));
    } else {
      setSelectedModels([...selectedModels, { platform, model: modelId }]);
    }
  }, [aiModels, selectedModels]);
  
  const handlePromptSelect = useCallback((promptId: string, promptText: string, promptName: string) => {
    setSelectedPromptId(promptId);
    setSelectedPromptText(promptText);
    setCustomPrompt(promptText);
    toast.success(`Selected prompt: ${promptName}`);
  }, []);
  
  const handleCustomPromptChange = useCallback((text: string) => {
    setCustomPrompt(text);
    if (text && selectedPromptId) {
      setSelectedPromptId(null);
      setSelectedPromptText('');
    }
  }, [selectedPromptId]);
  
  const clearSelection = useCallback(() => {
    setSelectedPromptId(null);
    setSelectedPromptText('');
    setCustomPrompt('');
    setSelectedFiles([]);
    setFirstFrameFiles([]);
    setLastFrameFiles([]);
    setUploadingFrame(null);
  }, []);
  
  const processGenerationRequest = async (
    generationType: string,
    models: Array<{ platform: string; model: string }>,
    options: GenerationOptions
  ) => {
    const processedResults: GenerationResult[] = [];

    for (let i = 0; i < models.length; i++) {
      const { platform, model } = models[i];
      setProgress((i / models.length) * 80);
      
      const result = createGenerationResult(generationType, platform, model, options.prompt, options.inputFiles || [], i);
      
      processedResults.push(result);
      setResults(prev => [...prev, result]);
      
      try {
        console.log(`Starting generation for model ${platform}/${model} with ID: ${result.id}`);
        
        const functionKey = `${generationType}_${platform}_${model}` as keyof typeof GENERATION_FUNCTIONS;
        const generatorFunction = GENERATION_FUNCTIONS[functionKey];
        
        if (!generatorFunction) {
          throw new Error(`No generation function found for ${functionKey}`);
        }

        const generationResult = await generatorFunction(options, generateVisual);

        if (!generationResult) {
          throw new Error('Generation failed');
        }

        console.log(`Generation completed for ID: ${result.id}`, generationResult);

        const successUpdates = handleGenerationSuccess(result.id, generationResult, result);
        setResults(prev => updateGenerationStatus(prev, result.id, 'completed', successUpdates));
        
      } catch (error) {
        console.error(`Error generating with ${platform}/${model}:`, error);
        const errorUpdates = handleGenerationError(error as Error);
        setResults(prev => updateGenerationStatus(prev, result.id, 'error', errorUpdates));
      }
    }
    
    return processedResults;
  };
  
  const handleSetAsPreview = useCallback(async (result: GenerationResult) => {
    try {
      if (!result.imageUrl) {
        toast.error('No image URL available');
        return;
      }

      const promptId = selectedPromptId;
      
      if (!promptId) {
        toast.error('Can only set preview for prompt-based generations');
        return;
      }

      const { error } = await supabase
        .from('os_art_prompts')
        .update({ preview: result.imageUrl })
        .eq('id', promptId);

      if (error) throw error;

      toast.success('Set as preview for this prompt');
    } catch (error) {
      console.error('Error setting as preview:', error);
      toast.error('Failed to set as preview');
    }
  }, [selectedPromptId]);
  
  const handleGenerate = useCallback(async () => {
    try {
      console.log('🚀 HANDLE GENERATE CALLED!');
      
      if ((selectedGenerationType === 'image2image' || selectedGenerationType === 'image2video') && selectedFiles.length === 0) {
        toast.error('Please upload at least one image first');
        return;
      }

      if (selectedGenerationType === 'morphing') {
        if (firstFrameFiles.length === 0 || lastFrameFiles.length === 0) {
          toast.error('Morphing requires both start and end frame images');
          return;
        }
      }
      
      if (selectedModels.length === 0) {
        toast.error('Please select at least one AI model');
        return;
      }
    
      const prompt = customPrompt || selectedPromptText;
      if (!prompt) {
        toast.error('Please enter a prompt or select one');
        return;
      }

      setIsGenerating(true);
      setProgress(0);
      setResults([]);

      const options: GenerationOptions = {
        prompt,
        promptId: selectedPromptId || null,
        inputFiles: selectedFiles.length > 0 ? selectedFiles : undefined,
        firstFrameFiles: selectedGenerationType === 'morphing' ? firstFrameFiles : undefined,
        lastFrameFiles: selectedGenerationType === 'morphing' ? lastFrameFiles : undefined,
        style: 'vivid',
        quality: 'standard',
        size: selectedVisualSize?.recommended_px || '1024x1024',
        generation_mode: generationMode,
        sequential_image_generation: generationMode === 'batch' ? 'auto' : 'disabled',
        sequential_image_generation_options: generationMode === 'batch' ? { max_images: maxImages } : undefined,
      };

      let actualGenerationType = selectedGenerationType;
      if (selectedGenerationType === 'all') {
        actualGenerationType = selectedFiles.length > 0 ? 'image2image' : 'text2image';
      }
      
      await processGenerationRequest(actualGenerationType, selectedModels, options);
      setProgress(100);
      
      setTimeout(() => {
        const successCount = results.filter(r => r.status === 'completed').length;
        const totalAttempted = selectedModels.length;
        
        if (successCount > 0) {
          toast.success(`Generated ${successCount} of ${totalAttempted} images successfully!`);
        } else {
          toast.error('Generation failed. Please try again.');
        }
        
        setIsGenerating(false);
        setProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error('🚨 ERROR in handleGenerate:', error);
      toast.error('Generation process failed. Please try again.');
      setIsGenerating(false);
      setProgress(0);
    }
  }, [
    selectedGenerationType, selectedFiles, firstFrameFiles, lastFrameFiles,
    selectedModels, customPrompt, selectedPromptText, selectedPromptId,
    selectedVisualSize, generationMode, maxImages, results, generateVisual
  ]);
  
  const value: VisualsCreationEditorContextType = {
    isOpen,
    openEditor,
    closeEditor,
    selectedPromptId,
    selectedPromptText,
    customPrompt,
    promptSearchQuery,
    setSelectedPromptId,
    setSelectedPromptText,
    setCustomPrompt,
    setPromptSearchQuery,
    selectedFiles,
    firstFrameFiles,
    lastFrameFiles,
    uploadingFrame,
    setSelectedFiles,
    setFirstFrameFiles,
    setLastFrameFiles,
    setUploadingFrame,
    isImageModalOpen,
    activeCarouselTab,
    isPreviewModalOpen,
    previewImageUrl,
    isPreviewVideo,
    setIsImageModalOpen,
    setActiveCarouselTab,
    setIsPreviewModalOpen,
    setPreviewImageUrl,
    setIsPreviewVideo,
    selectedSize,
    selectedVisualSize,
    setSelectedSize,
    setSelectedVisualSize,
    selectedModels,
    selectedGenerationType,
    setSelectedModels,
    setSelectedGenerationType,
    generationMode,
    maxImages,
    isGenerating,
    progress,
    results,
    showShortcodePopup,
    setGenerationMode,
    setMaxImages,
    setIsGenerating,
    setProgress,
    setResults,
    setShowShortcodePopup,
    handlePromptSelect,
    handleCustomPromptChange,
    handleModelSelect,
    handleGenerate,
    handleSetAsPreview,
    clearSelection,
    libraryPrompts,
    aiModels,
    validModelsForTab,
    sizes,
    sizesLoading,
    promptsLoading,
    keydateItems,
    keydatesLoading,
  };
  
  return (
    <VisualsCreationEditorContext.Provider value={value}>
      {children}
    </VisualsCreationEditorContext.Provider>
  );
};
